import fontManagerService from '@/utils/fontManagerService';
import { useColor } from '@/composables/color';

(function () {
    'ngInject';

    angular.module('app.shared.services').factory('MultipleFontService', multipleFontService);

    function multipleFontService(LibrariesAPIService, ElementsColorService, $mdDialog, AppCuesService) {
        const { getColorObj } = useColor();

        // textProperties.fontsMap/fontColorsMap coming from a live import (as opposed to a JSON
        // string from a saved template) can be either a Map or an array of [key, value] pairs;
        // copy its entries into `target` in place, since reassigning the local parameter passed
        // into initializeFontsMap/initializeFontColorsMap would not be visible to the caller.
        function copyMapEntries(target, source) {
            if (source instanceof Map) {
                source.forEach(function (value, key) {
                    target.set(key, value);
                });
            } else if (Array.isArray(source)) {
                source.forEach(function (entry) {
                    if (!Array.isArray(entry) || entry.length !== 2) return;
                    target.set(entry[0], entry[1]);
                });
            }
        }

        // Builds a colors Map from a stored value, tolerating every shape found in saved
        // templates: a Map, a JSON string, an array of [key, value] pairs, or a single
        // flattened pair ([1, color] instead of [[1, color]] - written by older clones).
        // `new Map(value)` throws on the flattened shape, and a throw here aborts the whole
        // template load, so anything unrecognizable degrades to an empty Map instead.
        function parseColorsMap(source) {
            if (source instanceof Map) return new Map(source);
            if (typeof source === 'string') {
                try {
                    source = JSON.parse(source);
                } catch {
                    return new Map();
                }
            }
            if (!Array.isArray(source)) return new Map();

            const isFlattenedPair =
                source.length === 2 && !Array.isArray(source[0]) && typeof source[0] !== 'object';
            const map = new Map();
            copyMapEntries(map, isFlattenedPair ? [source] : source);
            return map;
        }

        function initializeFontsMap(fontsMap, fontPath, textProperties) {
            fontsMap.set(1, fontPath);
            if (textProperties.fontsMap && typeof textProperties.fontsMap === 'string') {
                try {
                    JSON.parse(textProperties.fontsMap).map(function (element) {
                        fontsMap.set(element[0], element[1]);
                    });
                } catch {
                    // leave the default (position 1) already set above
                }
            } else if (textProperties.fontsMap) {
                copyMapEntries(fontsMap, textProperties.fontsMap);
            }
            return fontsMap;
        }

        function initializeFontColorsMap(fontColorsMap, textProperties) {
            if (textProperties.color && typeof textProperties.color === 'string') {
                try {
                    fontColorsMap.set(1, JSON.parse(textProperties.color));
                } catch {
                    fontColorsMap.set(1, textProperties.color);
                }
            } else {
                fontColorsMap.set(1, textProperties.color);
            }

            if (textProperties.fontColorsMap && typeof textProperties.fontColorsMap === 'string') {
                try {
                    JSON.parse(textProperties.fontColorsMap).forEach((element) => {
                        fontColorsMap.set(element[0], element[1]);
                    });
                } catch {
                    // leave the default (position 1) already set above
                }
            } else if (textProperties.fontColorsMap) {
                copyMapEntries(fontColorsMap, textProperties.fontColorsMap);
            }
            return fontColorsMap;
        }

        function saveFontToOption(fontPath, object, fontOption, editMode) {
            object.fontsMap.set(fontOption, fontPath);

            // This is to prevent having the last selected font saved as the default one
            //  in the cases where there are several fonts stored in the same text placeholder we force the one in position (1) to be the default one.
            // In other words, we store the first font in the fontsMap into fontPath.
            if (fontOption === 1) {
                object.fontPath = fontPath;
                object.firstFontName = object.fontName;
            }

            if (editMode) {
                object.setEditedProp('fontsMap', 'fontPath');
            }

            $mdDialog
                .show(
                    $mdDialog
                        .alert()
                        .parent(document.body)
                        .clickOutsideToClose(false)
                        .title('Font saved into option : ' + fontOption)
                        .ok('OK!')
                )
                .then(function () {
                    if (AppCuesService.getCurrentFlow() == AppCuesService.OptionBPart1 && fontOption == 1) {
                        window.Appcues?.show(AppCuesService.OptionBPart2);
                    }
                    if (AppCuesService.getCurrentFlow() == AppCuesService.OptionBPart3 && fontOption == 2) {
                        window.Appcues?.show(AppCuesService.OptionBPart4);
                    }
                });
        }

        function saveFontColorToOption(color, object, editMode, showInfoDialog = true) {
            var colorObj = createColorObj({
                alpha: color.alpha,
                hex: '#' + color.HEX,
                cmyk: color.cmyk.c + ',' + color.cmyk.m + ',' + color.cmyk.y + ',' + color.cmyk.k
            });

            object.fontColorOption = object.fontColorOption || 1;
            object.fontColorsMap.set(object.fontColorOption, colorObj);

            // This is to prevent having the last selected font saved as the default one
            //  in the cases where there are several fonts stored in the same text placeholder we force the one in position (1) to be the default one
            //  in other words, we store the first font in the fontsMap into fontPath
            if (object.fontColorOption === 1) {
                object.color = colorObj;
            }
            if (editMode) {
                object.setEditedProp('fontColorsMap', 'color');
            }

            if (showInfoDialog) {
                $mdDialog
                    .show(
                        $mdDialog
                            .alert()
                            .parent(document.body)
                            .clickOutsideToClose(false)
                            .title('Color saved into option: ' + object.fontColorOption)
                            .ok('OK!')
                    )
                    .then(function () {
                        if (AppCuesService.getCurrentFlow() == AppCuesService.OptionBPart4 && object.fontColorOption == 1) {
                            window.Appcues?.show(AppCuesService.OptionBPart5);
                        }
                        if (AppCuesService.getCurrentFlow() == AppCuesService.OptionBPart6 && object.fontColorOption == 2) {
                            window.Appcues?.show(AppCuesService.OptionBPart7);
                        }
                    });
            }
        }

        function saveFontColorToOptionPreview(color, object, editMode) {
            if (object.fontColorsMap) {
                saveFontColorToOption(color, object, editMode);
            } else {
                // If there is no fontColorsMap on the step2 text placeholder -> it's a pre multifont placeholder so we just set the basic color
                //  to whatever the user selected
                object.color = color;
            }
        }

        async function changeFontOption(textPlaceholder, fontOption, canvas) {
            var fontPathForOption;
            if (typeof fontOption === 'undefined') {
                return;
            }

            // Use options from fonts library if any
            if (textPlaceholder.fontLibraryId) {
                var fontObj = await LibrariesAPIService.getLibraryElementAtPosition(
                    textPlaceholder.fontLibraryId,
                    fontOption
                );
                if (fontObj) {
                    fontPathForOption = fontObj.Path;
                } else if (fontOption === 1) {
                    //If the first position in the font library is empty, the default font (Arial) is loaded. Otherwise, that font is not loaded, and the element cannot be rendered
                    fontPathForOption = textPlaceholder.fontsMap && textPlaceholder.fontsMap.get(fontOption);
                }
            } else {
                fontPathForOption = textPlaceholder.fontsMap && textPlaceholder.fontsMap.get(fontOption);
            }

            textPlaceholder.fontOption = fontOption;
            if (fontPathForOption) {
                textPlaceholder.fontPath = fontPathForOption;

                // Load the font and set it
                const fontName = await fontManagerService.loadFontFromPath(fontPathForOption);
                if (textPlaceholder.set) {
                    textPlaceholder.set('fontFamily', fontName);
                }
                textPlaceholder.fontName = fontName;
                canvas.renderAll();
            }
        }

        async function changeFontOptionPreview(textPlaceholder, fontOption, canvas) {
            var fontPathForOption;
            if (typeof fontOption === 'undefined') {
                return;
            }

            // Use options from fonts library if any
            if (textPlaceholder.fontLibraryId) {
                var fontObj = await LibrariesAPIService.getLibraryElementAtPosition(
                    textPlaceholder.fontLibraryId,
                    fontOption
                );
                if (fontObj) {
                    fontPathForOption = fontObj.Path;
                }
            } else {
                fontPathForOption = textPlaceholder.fontsMap && textPlaceholder.fontsMap.get(fontOption);
            }

            textPlaceholder.fontOption = fontOption;
            if (fontPathForOption) {
                textPlaceholder.fontPath = fontPathForOption;

                // Load the font and set it
                const fontName = await fontManagerService.loadFontFromPath(fontPathForOption);
                textPlaceholder.set('fontFamily', fontName);
                canvas.renderAll();
            } else {
                $mdDialog.show(
                    $mdDialog
                        .alert()
                        .parent(document.body)
                        .clickOutsideToClose(false)
                        .title('There is no font stored in option : ' + fontOption)
                        .ok('OK!')
                );
            }
        }

        /**
         * Set the given color position to a text element
         * @param {object | null} colorPicker color picker object to set the given color in it
         * @param {object} textSelected canvas text
         * @param {number} fontColorOption color option to set
         * @param {fabric.Canvas} canvas fabric canvas where the text element is loaded
         */
        async function changeFontColorOption(colorPicker, textSelected, fontColorOption, canvas) {
            let option;
            if (fontColorOption == undefined) {
                return;
            }

            textSelected.fontColorOption = fontColorOption;
            option = textSelected.colorLibraryId
                ? await LibrariesAPIService.getLibraryElementAtPosition(textSelected.colorLibraryId, fontColorOption)
                : textSelected.fontColorsMap?.get(fontColorOption);

            if (!option) return;

            const color = ElementsColorService.colorToWeb(option);
            textSelected.setColor(color);
            canvas.requestRenderAll();
        }

        /**
         * Set the given color position to a text element
         * @param {object} textSelected canvas text
         * @param {number} outlineColorOption color option to set
         * @param {fabric.Canvas} canvas fabric canvas where the text element is loaded
         */
        async function changeOutlineColorOption(textSelected, outlineColorOption, canvas) {
            if (outlineColorOption == undefined) return;

            textSelected.outlineColorOption = outlineColorOption;
            const option = textSelected.outlineColorLibraryId
                ? await LibrariesAPIService.getLibraryElementAtPosition(
                      textSelected.outlineColorLibraryId,
                      outlineColorOption
                  )
                : textSelected.outlineColorsMap?.get(outlineColorOption);

            if (!option) return;

            const color = ElementsColorService.colorToWeb(option);
            textSelected.setOutlineColor(color);
            canvas.renderAll();
        }

        async function initializeStrokeColor(strokeColorsMap, textProperties) {
            if (textProperties.strokeColorsMap && typeof textProperties.strokeColorsMap === 'string') {
                try {
                    JSON.parse(textProperties.strokeColorsMap).forEach((element) => {
                        let color = element[1];
                        if (typeof color === 'string') {
                            color = createColorObj({
                                hex: color
                            });
                        }
                        strokeColorsMap.set(element[0], color);
                    });
                } catch {
                    strokeColorsMap = textProperties.strokeColorsMap;
                }
            } else {
                strokeColorsMap = textProperties.strokeColorsMap;
            }
            if (!textProperties.strokeColor) {
                textProperties.strokeColor = createColorObj({
                    hex: '#000000'
                });
            }
        }

        function getShadowColorsMap(textProperties) {
            let shadowColorsMap = new Map();
            if (!textProperties.shadowColorsMap) {
                const shadowColorObj = getColorObj({ hex: '#7258E0', a: 1 }) ?? { hex: '#000000', a: 1, cmyk: {c : '0', m: '0', y:'0', k:'1'}};
                const defaultShadowColor = createColorObj({
                    hex: shadowColorObj.hex,
                    cmyk: `${shadowColorObj.cmyk.c},${shadowColorObj.cmyk.m},${shadowColorObj.cmyk.y},${shadowColorObj.cmyk.k}`,
                    a: shadowColorObj.a
                });
                shadowColorsMap.set(1, defaultShadowColor);
                return shadowColorsMap;
            }
            
            if (textProperties.shadowColorsMap && typeof textProperties.shadowColorsMap === 'string') {
                try {
                    JSON.parse(textProperties.shadowColorsMap).forEach((element) => {
                        shadowColorsMap.set(element[0], element[1]);
                    });
                } catch {
                    shadowColorsMap = textProperties.shadowColorsMap;
                }
            } else if (textProperties.shadowColorsMap) {
                shadowColorsMap = textProperties.shadowColorsMap;
            }
            return shadowColorsMap;
        }

        async function changeStrokeColorOption(textSelected, strokeColorOption, canvas) {
            if (strokeColorOption == undefined) {
                return;
            }

            textSelected.strokeColorOption = strokeColorOption;
            const option = textSelected.strokeColorLibraryId
                ? await LibrariesAPIService.getLibraryElementAtPosition(
                      textSelected.strokeColorLibraryId,
                      strokeColorOption
                  )
                : textSelected.strokeColorsMap?.get(strokeColorOption);

            if (!option) return;

            const color = ElementsColorService.colorToWeb(option);
            textSelected.setStrokeColor(color);
            canvas.requestRenderAll();
        }

        async function changeShadowColorOption(textSelected, shadowColorOption, canvas) {
            if (shadowColorOption == undefined) {
                return;
            }

            textSelected.shadowColorOption = shadowColorOption;
            const option = textSelected.shadowColorLibraryId
                ? await LibrariesAPIService.getLibraryElementAtPosition(
                      textSelected.shadowColorLibraryId,
                      shadowColorOption
                  )
                : textSelected.shadowColorsMap?.get(shadowColorOption);

            if (!option) return;

            const color = ElementsColorService.colorToWeb(option);
            textSelected.setShadowColor(color);
            canvas.requestRenderAll();
        }

        function createColorObj(override) {
            var colorObj = {
                alpha: 1,
                hex: '#000000',
                cmyk: '0,0,0,1'
            };

            colorObj = Object.assign(colorObj, override);
            return colorObj;
        }

        function parseColorFromProperty(property) {
            // Object color in JSON
            if (typeof property === 'string' && property.indexOf('{') > -1) {
                return JSON.parse(property);
            }
            // Hex color
            else if (typeof property === 'string') {
                return property;
            } else {
                return property;
            }
        }

        /**
         * Converts the old RGB string color to the new object representation if needed
         * @param {String|Object} color old RGB string color or new object color
         */
        function upgradeOldColor(color) {
            var obj = null;

            // RGB color
            if (typeof color === 'string' && color.indexOf('{') < 0) {
                // Color converter
                var converter = new Colors();

                // Hex representation
                if (color.indexOf('#') > -1) {
                    const cmyk = converter.convertColor(color.replace('#', ''), 'HEX2CMYK');

                    obj = createColorObj({
                        hex: color,
                        cmyk: cmyk.c / 100 + ',' + cmyk.m / 100 + ',' + cmyk.y / 100 + ',' + cmyk.k / 100
                    });
                }
                // rgba() representation
                else {
                    var rgba2hex = function (orig) {
                        var a,
                            rgb = orig.replace(/\s/g, '').match(/^rgba?\((\d+),(\d+),(\d+),?([^,\s)]+)?/i),
                            alpha = ((rgb && rgb[4]) || '').trim(),
                            hex = rgb
                                ? (rgb[1] | (1 << 8)).toString(16).slice(1) +
                                  (rgb[2] | (1 << 8)).toString(16).slice(1) +
                                  (rgb[3] | (1 << 8)).toString(16).slice(1)
                                : orig;

                        if (alpha !== '') {
                            a = alpha;
                        } else {
                            a = 1;
                        }

                        return {
                            hex: hex,
                            alpha: parseFloat(a)
                        };
                    };

                    var hexColor = rgba2hex(color);
                    const cmyk = converter.convertColor(hexColor.hex, 'HEX2CMYK');

                    obj = createColorObj({
                        alpha: hexColor.alpha,
                        hex: '#' + hexColor.hex,
                        cmyk: cmyk.c / 100 + ',' + cmyk.m / 100 + ',' + cmyk.y / 100 + ',' + cmyk.k / 100
                    });
                }
            } else {
                obj = color;
            }

            return obj;
        }

        /**
         * @param {Object} canvasText canvas text object (both text/curved).
         */
        function processTextBeforeSaving(canvasText) {
            let fontsMap = null;
            let fontColorsMap = null;
            let outlineColorsMap = null;
            let strokeColorsMap = null;
            let shadowColorsMap = null;

            if (canvasText.fontsMap) {
                if (typeof canvasText.fontsMap == 'string') {
                    fontsMap = canvasText.fontsMap;
                } else {
                    try {
                        // Set the first font on the map as the default one
                        canvasText.fontPath = canvasText.fontsMap.get(1);
                        // If it has something stored on the map -> transform it to JSON
                        fontsMap = JSON.stringify(Array.from(canvasText.fontsMap.entries()));
                    } catch {
                        fontsMap = canvasText.fontsMap;
                    }
                }
            }

            if (canvasText.fontColorsMap) {
                if (typeof canvasText.fontColorsMap == 'string') {
                    fontColorsMap = canvasText.fontColorsMap;
                } else {
                    try {
                        // Set the first color on the map as the default one
                        canvasText.color = canvasText.fontColorsMap.get(1);
                        // If it has something stored on the map -> transform it to JSON
                        fontColorsMap = JSON.stringify(Array.from(canvasText.fontColorsMap.entries()));
                    } catch {
                        fontColorsMap = canvasText.fontColorsMap;
                    }
                }
            }

            if (canvasText.outlineColorsMap) {
                outlineColorsMap =
                    typeof canvasText.outlineColorsMap == 'string'
                        ? canvasText.outlineColorsMap
                        : JSON.stringify(Array.from(canvasText.outlineColorsMap.entries()));
            }

            if (canvasText.strokeColorsMap) {
                strokeColorsMap =
                    typeof canvasText.strokeColorsMap == 'string'
                        ? canvasText.strokeColorsMap
                        : JSON.stringify(Array.from(canvasText.strokeColorsMap.entries()));
                if (typeof canvasText.strokeColorsMap == 'string') {
                    strokeColorsMap = canvasText.strokeColorsMap;
                } else {
                    try {
                        canvasText.strokeColor = canvasText.strokeColorsMap.get(1);
                        // If it has something stored on the map -> transform it to JSON
                        strokeColorsMap = JSON.stringify(Array.from(canvasText.strokeColorsMap.entries()));
                    } catch {
                        strokeColorsMap = canvasText.strokeColorsMap;
                    }
                }
            }
            if (canvasText.shadowColorsMap) {
                if (typeof canvasText.shadowColorsMap == 'string') {
                    shadowColorsMap = canvasText.shadowColorsMap;
                } else {
                    try {
                        // Set the first color on the map as the default one
                        canvasText.shadowColor = canvasText.shadowColorsMap.get(1);
                        // If it has something stored on the map -> transform it to JSON
                        shadowColorsMap = JSON.stringify(Array.from(canvasText.shadowColorsMap.entries()));
                    } catch {
                        shadowColorsMap = canvasText.shadowColorsMap;
                    }
                }
            }

            const color = typeof canvasText.color == 'string' ? canvasText.color : JSON.stringify(canvasText.color);
            const outlineColor =
                typeof canvasText.outlineColor == 'string'
                    ? canvasText.outlineColor
                    : JSON.stringify(canvasText.outlineColor);

            const strokeColor =
                typeof canvasText.strokeColor == 'string'
                    ? canvasText.strokeColor
                    : JSON.stringify(canvasText.strokeColor);

            const shadowColor =
                typeof canvasText.shadowColor == 'string'
                    ? canvasText.shadowColor
                    : JSON.stringify(canvasText.shadowColor);

            return { fontsMap, fontColorsMap, color, outlineColor, outlineColorsMap, strokeColor, strokeColorsMap, shadowColor, shadowColorsMap };
        }

        return {
            initializeFontsMap,
            initializeFontColorsMap,
            parseColorsMap,
            saveFontToOption,
            saveFontColorToOption,
            changeFontOption,
            changeFontOptionPreview,
            changeFontColorOption,
            saveFontColorToOptionPreview,
            createColorObj,
            parseColorFromProperty,
            upgradeOldColor,
            processTextBeforeSaving,
            initializeStrokeColor,
            changeStrokeColorOption,
            changeOutlineColorOption,
            changeShadowColorOption,
            getShadowColorsMap
        };
    }
})();
